import useSite from "./useSite";
import authService from "@/services/AuthService";
import { resolveTimeZone } from "@/utils/formatInSiteTz";

/** Product default when nothing else resolves — TIMES ALWAYS PACIFIC. */
export const DEFAULT_DISPLAY_TZ = "America/Los_Angeles";

/**
 * Returns the timezone to use for rendering dates/times.
 * Precedence: user profile override → site setting → America/Los_Angeles (PT).
 *
 * NEVER browser-local. Browser-local made every timestamp render in the viewer's own zone
 * (UTC on servers/CI/other regions), showing times hours off even though the stored data and
 * the site setting ("Pacific Standard Time") are correct. Per the standing rule, the platform
 * default is Pacific — a customer only sees a different zone if they explicitly set one.
 *
 * The value is always run through resolveTimeZone(), so it is guaranteed to be a valid IANA
 * zone — never a Windows TZ name or other string that would make Date.toLocaleString throw.
 */
export default function useResolvedTimezone(): string {
  const { site } = useSite();
  const userTz = (authService.profile as { timeZone?: string })?.timeZone;
  return resolveTimeZone(userTz || site?.timezone) || DEFAULT_DISPLAY_TZ;
}

/**
 * Timezone for INTERNAL admin-operator-only screens (scraper job logs, activity feeds, anything
 * an agent/admin looks at that a site visitor never sees). Deliberately skips the site.timezone
 * fallback that useResolvedTimezone() applies -- that fallback is correct for customer-facing
 * surfaces (chat transcripts, business-hours displays) where matching the site's own configured
 * timezone is the point, but it's wrong here: an admin operator's job-progress timestamps should
 * follow the operator (their profile override, else PT), never the site's customer-facing
 * business-hours timezone. Bug: scraper job timestamps for a site configured in a non-Pacific
 * timezone rendered in that site's zone instead of PT, violating the standing "TIMES ALWAYS
 * PACIFIC" rule (2026-08-25).
 */
export function useAdminOpsTimezone(): string {
  const userTz = (authService.profile as { timeZone?: string })?.timeZone;
  return resolveTimeZone(userTz) || DEFAULT_DISPLAY_TZ;
}
